import { isTimestampFromPastDay } from "../../../shared/utils/playbackHelpers";
import { shakaGetCurrentTime, playerGetDuration, playerCheckDynamicAsset } from "./playerCoreUtils";
import { playerTimeShift } from "./playerLiveUtils";
import PlayerConstants from "../../../shared/constants/player";
const { PLAYBACK_FORWARD_BUFFER, CIRCULAR_BUFFER_EDGE } = PlayerConstants;
const { RECORDING } = PlayerConstants.PLAYER_TYPE;

export const playerStartOverStreamHelper = (
  type,
  isLookbackStream,
  transitionLookbackStream,
  playerMgr,
  setIsReadyToChangeStreams,
  handleAVSStopContent,
  startingOverStream,
  triggerRestart,
  liveProgramDetails,
  recordingStartDeltaTime,
  displayToastNotification
) => {
  if (type === PlayerConstants.PLAYER_TYPE.VOD || (isLookbackStream && !transitionLookbackStream)) {
    // for on demand or catch up streams
    playerMgr.current?.seek(0);
  } else if (type === RECORDING) {
    // for already recorded streams
    playerMgr.current?.seek(recordingStartDeltaTime);

    if (playerCheckDynamicAsset(playerMgr)) {
      displayToastNotification("beginning_of_available_stream_reached");
    }
  } else {
    setIsReadyToChangeStreams(false);
    handleAVSStopContent(); // release stream count if user restarts the stream, to avoid concurrent stream errors
    startingOverStream.current = true;
    triggerRestart(liveProgramDetails);
  }
};

export const isLookbackAssetSeeking = (isRestartedStream, playerMgr, currentPos) => {
  return isRestartedStream && !playerMgr.current.isLive() && !isTimestampFromPastDay(currentPos);
};

export const lookbackSeekableEndPos = (currentPos, duration, startTime) => {
  const currentPlaybackSeconds = currentPos - startTime / 1000;
  return currentPlaybackSeconds + duration;
};

export const playerRestartedStreamProgress = (playerMgr, currentPlaybackTime, startTime, endTime, landTime) => {
  if (currentPlaybackTime > 0 && isTimestampFromPastDay(currentPlaybackTime)) {
    return (currentPlaybackTime / 1000 / playerMgr.current?.getDuration()) * 100;
  } else {
    return ((playerMgr?.current?.getCurrentTime() * 1000 - landTime.current) / (endTime - startTime)) * 100;
  }
};

export const playerUpdateLiveRestartProgress = (
  playerMgr,
  progress,
  isRestartTrickPlayAllowed,
  displayToastNotification,
  isSafari
) => {
  // VxOA 9.0 player time is relative to availabilityStartTime; progress is epoch seconds
  const isVxOA9 = isVxOA9RestartStream(playerMgr);
  const presentationStartTime = isVxOA9 ? getContentPresentationStartTime(playerMgr) : 0;
  const seekTarget = progress - presentationStartTime;
  if (seekTarget > shakaGetCurrentTime(playerMgr) && !isRestartTrickPlayAllowed.current) {
    displayToastNotification("message_fast_forward_disabled");
  } else {
    let target = isSafari ? seekTarget + PLAYBACK_FORWARD_BUFFER : seekTarget;
    const seekRange = isVxOA9 && playerMgr.current?.getSeekRange();
    if (seekRange) {
      target = Math.min(Math.max(target, seekRange.start), seekRange.end);
    }
    playerTimeShift(playerMgr, target);
  }
};

// Inverse of playerRestartedStreamProgress: the bar % is relative to the manifest duration, not the guide window
export const restartPercentToTimestamp = (playerMgr, progress) =>
  getContentPresentationStartTime(playerMgr) + (playerGetDuration(playerMgr) * progress) / 100;

// Shaka doesn't pull out-of-range seeks back on in-progress VxOA 9.0 DASH restarts, so clamp to the seek range
export const playerVxOA9RestartSeek = (playerMgr, newPos, displayToastNotification) => {
  const { start, end } = playerMgr.current.getSeekRange();
  if (newPos <= start) {
    displayToastNotification("beginning_of_available_stream_reached");
  } else if (newPos >= end) {
    displayToastNotification("live_position_reached");
  }
  playerTimeShift(playerMgr, Math.min(Math.max(newPos, start), end));
};

export const playerUpdateLookBackProgress = (
  playerMgr,
  progress,
  isRestartTrickPlayAllowed,
  displayToastNotification,
  setProgressStyle,
  seekPlayback
) => {
  const videoDuration = playerGetDuration(playerMgr);
  let videoPos = Math.floor((videoDuration / 100) * progress);
  if (videoPos > shakaGetCurrentTime(playerMgr) && !isRestartTrickPlayAllowed.current) {
    displayToastNotification("message_fast_forward_disabled");
  } else {
    setProgressStyle(progress);
    seekPlayback(videoPos, false);
  }
};

/**
 * Checks if a given number represents a valid UTC timestamp within circular buffer threshold (165 hours).
 * @param {number} timestamp - The number to check
 * @returns {boolean} - True if the number is a valid UTC timestamp within threshold, false otherwise
 */
export const isValidCircularBufferStartTime = (timestamp) => {
  // Check if the input is a number
  if (typeof timestamp !== "number" || isNaN(timestamp)) {
    return false;
  }

  // Handle both seconds and milliseconds timestamps
  const timestampInMs = timestamp < 1e12 ? timestamp * 1000 : timestamp;

  // Try to create a Date object and verify it's valid
  const date = new Date(timestampInMs);
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return false;
  }

  // Check if timestamp is within the circular buffer threshold
  const now = new Date();
  const circularBufferEdge = new Date(now.getTime() - CIRCULAR_BUFFER_EDGE);

  return date >= circularBufferEdge && date <= now;
};

/**
 * Gets the content presentation start time Unix Epoch time in seconds from the player reference.
 * This is used to determine the start time of the content for live streams.
 * @param {*} playerRef - Reference to the player instance
 * @returns - {number} - The content presentation start time in seconds
 */
export const getContentPresentationStartTime = (playerRef) => {
  const presentationStartTimeAsDate = playerRef.current?.getPresentationStartTimeAsDate();
  return presentationStartTimeAsDate ? presentationStartTimeAsDate.getTime() / 1000 : 0; // convert to seconds
};

/**
 * Gets the content playhead Unix Epoch time in seconds from the player reference.
 * This is used to determine the current playback position in the content.
 * @param {*} playerRef - Reference to the player instance
 * @returns - {number} - The current playhead time in seconds
 */
export const getContentPlayheadTime = (playerRef) => {
  const playheadTimeAsDate = playerRef.current?.getPlayheadTimeAsDate();
  return playheadTimeAsDate ? playheadTimeAsDate.getTime() : 0; // milliseconds
};

/**
 * gets the content start seek time based on the player reference and default start time.
 * If the availabilityStartTime is set in the manifest, it calculates the seek time relative to that.
 * Otherwise, it uses the default start time or the stream's seek range start.
 * @param {*} playerRef - Reference to the player instance
 * @param {*} defaultStartTime - Default start time to use if availabilityStartTime is not set
 * @returns - {number} - The calculated content start seek time in seconds
 */
export const getContentStartSeekTime = (playerRef, defaultStartTime) => {
  // determine if availabilityStartTime is set in live restart manifest, this is introduced in VxOA 9.0 update. With this property set to accurate program start time, Shaka will retrieve seek range in seconds relative to this UTC time
  const presentationStartTime = getContentPresentationStartTime(playerRef);
  const isAvailabilityStartTimeSetInManifest = isValidCircularBufferStartTime(presentationStartTime);
  let programStartSeekTime = defaultStartTime;
  if (isAvailabilityStartTimeSetInManifest) {
    const streamSeekRange = playerRef.current.getSeekRange();
    if (
      programStartSeekTime > presentationStartTime &&
      programStartSeekTime - presentationStartTime > streamSeekRange.start
    ) {
      programStartSeekTime = programStartSeekTime - presentationStartTime;
    } else {
      programStartSeekTime = streamSeekRange.start;
    }
  }
  return programStartSeekTime;
};

/**
 * Checks if the availabilityStartTime is set in the manifest.
 * This is introduced in VxOA 9.0 update. With this property set to accurate program start time,
 * Shaka will retrieve seek range in seconds relative to this UTC time.
 * @param {*} playerRef - Reference to the player instance
 * @returns {boolean} - True if availabilityStartTime is set, false otherwise
 */
export const hasAvailabilityStartTimeSetInManifest = (playerRef) => {
  const presentationStartTime = getContentPresentationStartTime(playerRef);
  return isValidCircularBufferStartTime(presentationStartTime);
};

/**
 * Checks if the player loaded manifest a VxOA 9.0+ live stream.
 * This is determined by checking if the availabilityStartTime is set in the manifest
 * and if the player is currently live or in progress.
 * @param {*} playerRef - Reference to the player instance
 * @returns {boolean} - True if it is a VxOA 9.0+ live stream, false otherwise
 */
export const isVxOA9RestartStream = (playerRef) => {
  return (
    hasAvailabilityStartTimeSetInManifest(playerRef) &&
    (playerRef.current?.isLive() || playerRef.current?.isInProgress())
  );
};
